import {
	FormField,
	FormSection,
	FormSubSection,
	FieldResponseStatus,
} from '@models/client-intake';
import {
	ReviewResponseItem,
	IntakeResponseSection,
} from '../view-models/models';

export function flattenFormFields(sections: FormSection[]): FormField[] {
	const fields: FormField[] = [];

	function extractFields(items: (FormField | FormSubSection)[]): void {
		for (const item of items) {
			if ('type' in item) {
				// It's a FormField
				fields.push(item);
			} else {
				// It's a FormSubSection
				extractFields(item.fields);
			}
		}
	}

	sections.forEach((section) => {
		extractFields(section.fields);
	});

	return fields;
}

export function filterItemsByFormAndTab(
	allItems: ReviewResponseItem[],
	formId: string | null,
	currentTab: string,
): ReviewResponseItem[] {
	if (!formId) return [];

	// Filter by selected form
	const formItems = allItems.filter((item) => item.clientId === formId);

	// Filter by current tab
	if (currentTab === 'all') {
		return formItems;
	}

	return formItems.filter((item) => item.status === currentTab);
}

export function applyCheckboxStates(
	items: ReviewResponseItem[],
	checkboxStatesValue: Record<string, Record<string, boolean>>,
	formId: string | null,
): ReviewResponseItem[] {
	if (!formId) return [];

	return items.map((item) => ({
		...item,
		isSelected: checkboxStatesValue[formId]?.[item.questionId] || false,
	}));
}

export function calculateStatusCounts(items: ReviewResponseItem[]) {
	const counts = {
		all: items.length,
		[FieldResponseStatus.READY_TO_REVIEW]: 0,
		[FieldResponseStatus.ADDED_TO_MATTER]: 0,
		[FieldResponseStatus.MISSING_RESPONSES]: 0,
	};

	items.forEach((item) => {
		counts[item.status]++;
	});

	return counts;
}

export function groupItemsIntoSections(
	items: ReviewResponseItem[],
): IntakeResponseSection[] {
	if (items.length === 0) {
		return [];
	}

	// Group items by sectionTitle (from response metadata)
	const sectionMap = new Map<string, ReviewResponseItem[]>();

	items.forEach((item) => {
		if (!sectionMap.has(item.sectionTitle)) {
			sectionMap.set(item.sectionTitle, []);
		}
		const sectionItems = sectionMap.get(item.sectionTitle);
		if (sectionItems) {
			sectionItems.push(item);
		}
	});

	// Convert to array and sort by sectionOrder, then by fieldOrder within each section
	const sections: IntakeResponseSection[] = Array.from(sectionMap.entries())
		.map(([title, sectionItems]) => ({
			title,
			sectionOrder: sectionItems[0]?.sectionOrder ?? 0,
			data: sectionItems.sort((a, b) => a.fieldOrder - b.fieldOrder),
		}))
		.sort((a, b) => a.sectionOrder - b.sectionOrder)
		.map((section) => ({
			title: section.title,
			data: section.data,
		}));

	return sections;
}

export function toggleItemSelection(
	currentStates: Record<string, Record<string, boolean>>,
	formId: string,
	questionId: string,
	selected: boolean,
): Record<string, Record<string, boolean>> {
	const formStates = currentStates[formId] || {};

	return {
		...currentStates,
		[formId]: {
			...formStates,
			[questionId]: selected,
		},
	};
}

export function selectAllForForm(
	currentStates: Record<string, Record<string, boolean>>,
	formId: string,
	items: ReviewResponseItem[],
	selected: boolean,
): Record<string, Record<string, boolean>> {
	const existingFormStates = currentStates[formId] || {};
	const updatedFormStates = { ...existingFormStates };

	items.forEach((item) => {
		if (
			item.status === FieldResponseStatus.MISSING_RESPONSES ||
			item.status === FieldResponseStatus.ADDED_TO_MATTER
		) {
			return;
		}

		updatedFormStates[item.questionId] = selected;
	});

	return {
		...currentStates,
		[formId]: updatedFormStates,
	};
}

export function getSelectedItemsForSubmission(
	formId: string,
	items: ReviewResponseItem[],
) {
	return items
		.filter((item) => item.isSelected && item.clientResponse)
		.map((item) => ({
			questionId: item.questionId,
			answer: item.clientResponse,
			mapTo: item.mapTo ?? null,
			uploadOnlyConfig: item.uploadOnlyConfig ?? null,
		}));
}
